import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "../CommonComponents/genericModal/GenericModal";
import ToggleSwitch from "../CommonComponents/toggleSwitch/ToggleSwitch";
import styles from "./users-and-roles.module.css";
import CrossIcon from "../Icons/cross-icon";
import LeftArrow from "../Icons/left-arrow";
import RightArrow from "../Icons/right-arrow";

const RoleNamePage = ({ roleData, setRoleData, setNextDisabled }) => {
  useEffect(() => {
    setNextDisabled(roleData.name.trim().length === 0);
  }, [roleData.name]);

  const handleRoleNameChange = (value) => {
    setRoleData({ ...roleData, name: value });
  } 

  return (
    <>
      <div className={styles.roleModalSection}>
        <div className={styles.roleNameHeader}>
          <div className={styles.roleModalSectionTitle}>Role name</div>
          <div className={styles.roleModalSectionDescription}>
            Choose a short, descriptive name for this role.
          </div>
        </div>
      </div>
      <div className={styles.roleModalForm}>
        <input 
          type="text"
          className={styles.roleModalInput}
          placeholder="E.g. Manager, Supervisor, etc."
          value={roleData.name}
          onChange={(e) => handleRoleNameChange(e.target.value)}
        />
      </div>
    </>
  );
};

const PermissionsPage = ({config, roleData, setRoleData, setNextDisabled}) => {
  useEffect(() => {
    setNextDisabled(false);
  }, []);

  return (
    <>
      <div className={styles.roleModalPermissions}>
        <div className={styles.roleModalSection}>
          <div className={styles.roleModalSectionTitle}>{config.title}</div>
          <div className={styles.roleModalSectionDescription}>
            {config.description}
          </div>
        </div>
        {config.permissions.map(permObj => 
        <div className={styles.roleModalPermissionItem}>
          <div className={styles.roleModalPermissionInfo}>
            <div className={styles.roleModalPermissionTitle}>{permObj.title}</div>
            <div className={styles.roleModalPermissionDescription}>{permObj.description}</div>
          </div>
          <ToggleSwitch 
            checked={roleData.permissions.includes(permObj.value)}
            onChange={(checked) => {
              if(checked) {
                setRoleData({ ...roleData, permissions: [...roleData.permissions, permObj.value] });
              } else {
                setRoleData({ ...roleData, permissions: roleData.permissions.filter(p => p !== permObj.value) });
              }
            }}
          />
        </div>)}
      </div>
    </>
  )
}

const config1 = {
  title: "General access",
  description: "Control what this role can see and manage across the platform.",
  permissions: [
    {
      title: "Seeing reviews and answering them",
      description: "View and reply to customer reviews.",
      value: "reviews:manage",
    },
    {
      title: "Seeing reports (order report, heatmap)",
      description: "Access analytics, order reports, and heatmaps.",
      value: "reports:read",
    },
    {
      title: "Email notifications management",
      description: "Decide which email notifications are sent and to whom.",
      value: "notification:manage",
    },
  ],
}

const config2 = {
  title: "Account and billing access",
  description: "Define what this role can change or access in accounts and billing.",
  permissions: [
    {
      title: "Adding card and assigning that to locations",
      description: "Add payment cards and link them to locations.",
      value: "payment:manage",
    },
    {
      title: "Logging in as individual locations",
      description: "Access and operate as a specific location.",
      value: "company:access",
    },
    {
      title: "Enterprise user + role management",
      description: "Create, edit, and remove enterprise users and roles.",
      value: "user:manage",
    },
    {
      title: "Location bill details",
      description: "View billing statements and payment details for each location.",
      value: "billing:read",
    },
  ],
}

const components = [
  RoleNamePage,
  (props) => <PermissionsPage config={config1} {...props} />,
  (props) => <PermissionsPage config={config2} {...props} />,
]

const RoleModal = ({ open, onClose, roleId, roles, onCreate, onUpdate, isCreating, isUpdating }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [roleData, setRoleData] = useState({
    id: null,
    name: "",
    permissions: [],
  });

  const resetRoleData = () => {
    if (roleId) {
      const role = roles.getRoleById(roleId);
      if (!role) return;

      setRoleData({
        id: role.getId(),
        name: role.getName(),
        permissions: role.getPermissions().getActivePermissions(),
      });
    } else {
      setRoleData({
        id: null,
        name: "",
        permissions: [],
      });
    }
  }

  useEffect(() => {
    if(!open) {
      const timeout = setTimeout(() => {
        setCurrentPage(0);
        setNextDisabled(false);
        resetRoleData();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    resetRoleData();
  }, [roleId, roles]);

  const handleNext = () => {
    if (currentPage + 1 < components.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const Component = components[currentPage];


  const handleClose = () => {
    onClose();
  };

  const handleCreate = () => {
    if (roleId == null) {
      onCreate(roleData);
    } else {
      onUpdate({ roleId, ...roleData });
    }
  };

  const nextButtonText = useMemo(() => {
    if (currentPage + 1 < components.length) {
      return "Continue";
    } else {
      if (roleId === null) {
        return isCreating ? "Creating role..." : "Create role";
      } else {
        return isUpdating ? "Updating role..." : "Update role";
      }
    }
  }, [currentPage, isCreating, isUpdating, roleId]);

  const titleText = roleId ? "Edit role" : "Create new role";
  const descriptionText = roleId ? "Update the role name and adjust permissions.." : "Create a role with customized permissions.";

  return (
    <GenericModal 
      open={open} 
      onClose={handleClose} 
      backdropClassName={styles.modalBackdrop} 
      modalClassName={styles.roleModal}
    >
      <div className={styles.roleModalContainer}>
        <div className={styles.roleModalHeader}>
            <div className={styles.roleModalTitle}>
              <div className={styles.roleModalTitleText}>{titleText}</div>
              <div className={styles.roleModalTitleDescription}>
                {descriptionText}
              </div>
            </div>
            <button className={styles.roleModalCloseButton} onClick={handleClose}>
              <CrossIcon />
            </button>
        </div>

        <div className={styles.roleModalContent}>
          <Component roleData={roleData} setRoleData={setRoleData} setNextDisabled={setNextDisabled} />          
        </div>

        <div className={styles.roleModalProgressBar}>
          <div className={styles.roleModalProgressFill} style={{ width: `${(100 / components.length) * (currentPage + 1)}%` }}></div>
        </div>

        <div className={styles.roleModalFooter}>
          <div className={styles.roleModalFooterLeft}>
            {currentPage > 0 && (
              <button className={styles.roleModalBackButton} onClick={handleBack}>
                <LeftArrow />
                <div>Back</div>
              </button>
            )}
          </div>
          <div className={styles.roleModalFooterCenter}>
            <span className={styles.roleModalStepText}>{currentPage + 1} of {components.length}</span>
          </div>
          <div className={styles.roleModalFooterRight}>
            <button className={styles.roleModalNextButton} onClick={currentPage + 1 < components.length ? handleNext : handleCreate} disabled={nextDisabled || isCreating || isUpdating}>
              <div>{nextButtonText}</div>
              <RightArrow fill={nextDisabled ? "#A3A3A3" : "white"} />
            </button>            
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

export default RoleModal;
